import { Flex, Text } from '@chakra-ui/react'
import { ConditionalValue } from '@chakra-ui/react'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { PhoneStatus } from '~/atoms/WebphoneState'
import { Tooltip } from '~/components/ui/tooltip'
import { useClipboard } from '~/hooks/useClipboard'
import { Size } from '~/types/ThemeTypes'
import {
  convertCallDurationNoMilliseconds,
  formatPhoneNumber,
} from '~/utils/formatter'

import { useCallHeaderStyles } from './componentStyles/CallHeader.style'

function getCallStatusLabel(
  isOnHold: boolean,
  callStatus: PhoneStatus,
  t: (key: string) => string,
) {
  if (isOnHold) {
    return t('callStatus.onHold')
  }

  switch (callStatus) {
    case PhoneStatus.CALL_CONNECTING:
      return t('callStatus.connecting')
    case PhoneStatus.RINGING:
      return t('callStatus.ringing')
    case PhoneStatus.CALL_ACTIVE:
      return t('callStatus.connected')
    default:
      return t('callStatus.ready')
  }
}

interface CallHeaderProps {
  callerNames: string[]
  phonenumber?: string
  dtmfHistory: string
  callStartTime: Date | null
  isOnHold: boolean
  callStatus: PhoneStatus
  size: ConditionalValue<Size.sm | Size.md | Size.lg>
}
export const CallHeader = ({
  callerNames,
  phonenumber,
  dtmfHistory,
  callStartTime,
  isOnHold,
  callStatus,
  size,
}: CallHeaderProps) => {
  const { t } = useTranslation()
  const [callDuration, setCallDuration] = React.useState<string>('0:00')
  const {
    copyToClipboard: copyToClipboardCallerId,
    hasCopied: hasCopiedCallerId,
  } = useClipboard()
  const {
    copyToClipboard: copyToClipboardPhoneNumber,
    hasCopied: hasCopiedPhoneNumber,
  } = useClipboard()

  // Create an interval to track call duration and update label during call.
  // Depends on callStartTime
  useEffect(() => {
    const updateCallDuration = () => {
      if (!callStartTime) {
        return
      }
      const callDurationInSeconds =
        (new Date().getTime() - callStartTime.getTime()) / 1000
      const callDurationString = convertCallDurationNoMilliseconds(
        callDurationInSeconds,
      )
      setCallDuration(callDurationString)
    }

    const interval = setInterval(() => updateCallDuration(), 1000)
    return () => clearInterval(interval)
  }, [callStartTime])

  const style = useCallHeaderStyles({ size })

  const { namesTextElements, joinedNames } = useMemo(() => {
    const joinedNames = callerNames.join(' & ')
    if (callerNames.length === 0) {
      return {
        joinedNames,
        namesTextElements: (
          <Text css={style.name}>{t('webPhone.unknownCaller')}</Text>
        ),
      }
    }
    if (callerNames.length === 1) {
      return {
        joinedNames,
        namesTextElements: <Text css={style.name}>{callerNames[0]}</Text>,
      }
    }
    return {
      joinedNames,
      namesTextElements: callerNames.map((name, index) => (
        <Text key={index} css={style.name}>
          {name} {index < callerNames.length - 1 ? ' & ' : ''}
        </Text>
      )),
    }
  }, [callerNames, t, style.name])

  const label = getCallStatusLabel(isOnHold, callStatus, t)

  // Extract layout properties to apply as native props (Chakra v3 requirement)
  const containerProps = {
    flexDirection: style.container?.flexDirection,
    alignItems: style.container?.alignItems,
    justifyContent: style.container?.justifyContent,
  }

  return (
    <Flex css={style.container} {...containerProps}>
      <Tooltip
        content={
          hasCopiedCallerId
            ? t('webPhone.tooltip.copied')
            : t('webPhone.tooltip.copyCallerId')
        }
        closeOnPointerDown={false}
        positioning={{ placement: 'top' }}
        showArrow
      >
        <div
          onClick={() => copyToClipboardCallerId(joinedNames)}
          style={{ cursor: 'pointer' }}
        >
          {namesTextElements}
        </div>
      </Tooltip>
      <Flex>
        {phonenumber && (
          <Tooltip
            content={
              hasCopiedPhoneNumber
                ? t('webPhone.tooltip.copied')
                : t('webPhone.tooltip.copyPhoneNumber')
            }
            closeOnPointerDown={false}
            showArrow
          >
            <Text
              css={{ ...style.number, cursor: 'pointer' }}
              onClick={() => copyToClipboardPhoneNumber(phonenumber)}
            >
              {formatPhoneNumber(phonenumber) + ' - '}
            </Text>
          </Tooltip>
        )}
        <Text css={style.status}>{label}</Text>
        {callStatus === PhoneStatus.CALL_ACTIVE && (
          <Text css={style.time}>{callDuration}</Text>
        )}
      </Flex>
      <Text css={style.dtmf}>{dtmfHistory}</Text>
    </Flex>
  )
}
